import React, { useState, useEffect } from 'react';
import { Plus, X, Search, Download, Calendar, Users, Briefcase, AlertCircle, LogOut } from 'lucide-react';

// CONFIGURED FOR crm.getofficegenie.com
const API_BASE = 'https://crm.getofficegenie.com/api.php';

const BusinessCRM = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [showClientForm, setShowClientForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  const [clientForm, setClientForm] = useState({ name: '', company: '', email: '', phone: '', notes: '' });
  const [projectForm, setProjectForm] = useState({ name: '', clientId: '', status: 'active', startDate: '', endDate: '', deliverables: '', documentLinks: '' });
  const [taskForm, setTaskForm] = useState({ title: '', dueDate: '', assignee: '', priority: 'medium', clientId: '', status: 'pending' });

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('crmUser');
    const savedToken = localStorage.getItem('crmToken');
    if (savedUser && savedToken) {
      const userData = JSON.parse(savedUser);
      setCurrentUser(userData);
      setAuthMode('authenticated');
      loadAllData(savedToken);
    }
  }, []);

  // API Helper Functions
  const apiCall = async (action, method = 'GET', data = null, token = null) => {
    try {
      const options = {
        method,
        headers: { 'Content-Type': 'application/json' }
      };

      let url = `${API_BASE}?action=${action}`;
      if (token) url += `&token=${token}`;
      if (data) options.body = JSON.stringify(data);

      const response = await fetch(url, options);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('API Error:', error);
      return { error: error.message };
    }
  };

  const loadAllData = async (token) => {
    const clientsRes = await apiCall('clients', 'GET', null, token);
    const projectsRes = await apiCall('projects', 'GET', null, token);
    const tasksRes = await apiCall('tasks', 'GET', null, token);

    if (clientsRes.clients) setClients(clientsRes.clients);
    if (projectsRes.projects) setProjects(projectsRes.projects);
    if (tasksRes.tasks) setTasks(tasksRes.tasks);
  };

  // Authentication
  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setAuthError('Please enter email and password');
      return;
    }

    setLoading(true);
    const result = await apiCall('auth', 'POST', {
      authAction: 'login',
      email: loginEmail,
      password: loginPassword
    });

    if (result.error) {
      setAuthError(result.error);
    } else if (result.success) {
      const userData = result.user;
      const token = result.token;

      setCurrentUser(userData);
      localStorage.setItem('crmUser', JSON.stringify(userData));
      localStorage.setItem('crmToken', token);
      setAuthMode('authenticated');
      setLoginEmail('');
      setLoginPassword('');
      setAuthError('');
      loadAllData(token);
    }
    setLoading(false);
  };

  const handleSignup = async () => {
    if (!signupEmail || !signupPassword) {
      setAuthError('Please enter email and password');
      return;
    }

    if (signupPassword.length < 6) {
      setAuthError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await apiCall('auth', 'POST', {
      authAction: 'signup',
      email: signupEmail,
      password: signupPassword
    });

    if (result.error) {
      setAuthError(result.error);
    } else if (result.success) {
      const userData = result.user;
      const token = result.token;

      setCurrentUser(userData);
      localStorage.setItem('crmUser', JSON.stringify(userData));
      localStorage.setItem('crmToken', token);
      setAuthMode('authenticated');
      setSignupEmail('');
      setSignupPassword('');
      setAuthError('');
      loadAllData(token);
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('crmUser');
    localStorage.removeItem('crmToken');
    setAuthMode('login');
    setClients([]);
    setProjects([]);
    setTasks([]);
  };

  // Get token
  const getToken = () => localStorage.getItem('crmToken');

  // Client operations
  const addClient = async () => {
    if (!clientForm.name) return;

    const result = await apiCall('clients', 'POST', clientForm, getToken());
    if (result.success) {
      setClients([...clients, { id: result.id, ...clientForm }]);
      setClientForm({ name: '', company: '', email: '', phone: '', notes: '' });
      setShowClientForm(false);
    }
  };

  const deleteClient = async (id) => {
    await apiCall('clients', 'DELETE', { id }, getToken());
    setClients(clients.filter(c => c.id !== id));
    setProjects(projects.filter(p => p.client_id !== id));
  };

  // Project operations
  const addProject = async () => {
    if (!projectForm.name || !projectForm.clientId) return;

    const result = await apiCall('projects', 'POST', projectForm, getToken());
    if (result.success) {
      setProjects([...projects, { id: result.id, ...projectForm }]);
      setProjectForm({ name: '', clientId: '', status: 'active', startDate: '', endDate: '', deliverables: '', documentLinks: '' });
      setShowProjectForm(false);
    }
  };

  const updateProjectStatus = async (id, status) => {
    await apiCall('projects', 'PUT', { id, status }, getToken());
    setProjects(projects.map(p => p.id === id ? { ...p, status } : p));
  };

  const deleteProject = async (id) => {
    await apiCall('projects', 'DELETE', { id }, getToken());
    setProjects(projects.filter(p => p.id !== id));
  };

  // Task operations
  const addTask = async () => {
    if (!taskForm.title) return;

    const result = await apiCall('tasks', 'POST', taskForm, getToken());
    if (result.success) {
      setTasks([...tasks, { id: result.id, ...taskForm }]);
      setTaskForm({ title: '', dueDate: '', assignee: '', priority: 'medium', clientId: '', status: 'pending' });
      setShowTaskForm(false);
    }
  };

  const updateTaskStatus = async (id, status) => {
    await apiCall('tasks', 'PUT', { id, status }, getToken());
    setTasks(tasks.map(t => t.id === id ? { ...t, status } : t));
  };

  const deleteTask = async (id) => {
    await apiCall('tasks', 'DELETE', { id }, getToken());
    setTasks(tasks.filter(t => t.id !== id));
  };

  // Export
  const exportData = () => {
    const clientsCSV = 'Name,Company,Email,Phone,Notes\n' +
      clients.map(c => `"${c.name}","${c.company}","${c.email}","${c.phone}","${c.notes}"`).join('\n');

    const projectsCSV = 'Project,Client,Status,Start,End,Deliverables\n' +
      projects.map(p => {
        const client = clients.find(c => c.id == p.client_id);
        return `"${p.name}","${client?.name || 'N/A'}","${p.status}","${p.startDate}","${p.endDate}","${p.deliverables}"`;
      }).join('\n');

    const tasksCSV = 'Task,Due Date,Assignee,Priority,Status\n' +
      tasks.map(t => `"${t.title}","${t.dueDate}","${t.assignee}","${t.priority}","${t.status}"`).join('\n');

    const blob = new Blob([`CLIENTS:\n${clientsCSV}\n\nPROJECTS:\n${projectsCSV}\n\nTASKS:\n${tasksCSV}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CRM-Export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Filters
  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTasks = tasks.filter(t =>
    t.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getClientName = (id) => clients.find(c => c.id == id)?.name || 'Unknown';

  const activeProjects = projects.filter(p => p.status === 'active').length;
  const pendingTasks = tasks.filter(t => t.status === 'pending').length;
  const overdueTasks = tasks.filter(t => t.status === 'pending' && new Date(t.dueDate) < new Date()).length;

  // LOGIN VIEW
  if (authMode !== 'authenticated') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Briefcase className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900">Business CRM</h1>
          </div>

          {authError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{authError}</p>
            </div>
          )}

          {authMode === 'login' ? (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Sign In</h2>
              <input
                type="email"
                placeholder="Email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                placeholder="Password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                onClick={() => setAuthMode('signup')}
                className="w-full bg-slate-100 text-slate-900 py-3 rounded-lg font-medium hover:bg-slate-200 transition"
              >
                Create New Account
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-900 mb-6">Create Account</h2>
              <input
                type="email"
                placeholder="Email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                placeholder="Password (min 6 characters)"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSignup}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <button
                onClick={() => setAuthMode('login')}
                className="w-full bg-slate-100 text-slate-900 py-3 rounded-lg font-medium hover:bg-slate-200 transition"
              >
                Back to Sign In
              </button>
            </div>
          )}

          <p className="text-xs text-slate-500 text-center mt-6">
            Self-hosted on crm.getofficegenie.com
          </p>
        </div>
      </div>
    );
  }

  // CRM APP VIEW
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Business CRM</h1>
                <p className="text-sm text-slate-500">crm.getofficegenie.com</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={exportData} className="flex items-center gap-2 bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition">
                <Download className="w-4 h-4" />
                Export Data
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition">
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg">
            <Search className="w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search clients, projects, tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent flex-1 outline-none text-slate-900 placeholder-slate-500"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 border-t border-slate-200">
          {['dashboard', 'clients', 'projects', 'tasks'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 font-medium capitalize transition border-b-2 ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Dashboard</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                <p className="text-slate-600 text-sm font-medium">Total Clients</p>
                <p className="text-3xl font-bold text-slate-900">{clients.length}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                <p className="text-slate-600 text-sm font-medium">Active Projects</p>
                <p className="text-3xl font-bold text-slate-900">{activeProjects}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
                <p className="text-slate-600 text-sm font-medium">Pending Tasks</p>
                <p className="text-3xl font-bold text-slate-900">{pendingTasks}</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-500">
                <p className="text-slate-600 text-sm font-medium">Overdue Tasks</p>
                <p className="text-3xl font-bold text-slate-900">{overdueTasks}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4">Recent Tasks</h3>
                {tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.slice(-5).reverse().map(task => (
                      <div key={task.id} className="flex items-start gap-3 pb-3 border-b border-slate-200 last:border-0">
                        <div className={`w-2 h-2 rounded-full mt-2 ${task.status === 'completed' ? 'bg-green-500' : task.priority === 'high' ? 'bg-red-500' : 'bg-yellow-500'}`}></div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{task.title}</p>
                          <p className="text-sm text-slate-500">{task.dueDate}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">No tasks yet</p>
                )}
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4">Active Projects</h3>
                {projects.filter(p => p.status === 'active').length > 0 ? (
                  <div className="space-y-3">
                    {projects.filter(p => p.status === 'active').slice(-5).map(project => (
                      <div key={project.id} className="pb-3 border-b border-slate-200 last:border-0">
                        <p className="font-medium text-slate-900">{project.name}</p>
                        <p className="text-sm text-slate-500">{getClientName(project.client_id)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">No active projects</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CLIENTS TAB */}
        {activeTab === 'clients' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Clients</h2>
              <button
                onClick={() => setShowClientForm(!showClientForm)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                <Plus className="w-4 h-4" />
                Add Client
              </button>
            </div>

            {showClientForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Client Name" value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="text" placeholder="Company" value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="email" placeholder="Email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="tel" placeholder="Phone" value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <textarea placeholder="Notes" value={clientForm.notes} onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2" />
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={addClient} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">Save Client</button>
                  <button onClick={() => setShowClientForm(false)} className="bg-slate-300 text-slate-900 px-4 py-2 rounded-lg hover:bg-slate-400 transition">Cancel</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map(client => (
                <div key={client.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{client.name}</h3>
                      <p className="text-sm text-slate-600">{client.company}</p>
                    </div>
                    <button onClick={() => deleteClient(client.id)} className="text-slate-400 hover:text-red-600 transition"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    {client.email && <p><span className="font-medium">Email:</span> {client.email}</p>}
                    {client.phone && <p><span className="font-medium">Phone:</span> {client.phone}</p>}
                    {client.notes && <p><span className="font-medium">Notes:</span> {client.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
            {filteredClients.length === 0 && <p className="text-center text-slate-500 py-12">No clients yet.</p>}
          </div>
        )}

        {/* PROJECTS TAB */}
        {activeTab === 'projects' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Projects</h2>
              <button onClick={() => setShowProjectForm(!showProjectForm)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
                <Plus className="w-4 h-4" />
                New Project
              </button>
            </div>

            {showProjectForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Project Name" value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <select value={projectForm.clientId} onChange={(e) => setProjectForm({ ...projectForm, clientId: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Client</option>
                    {clients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                  <input type="date" value={projectForm.startDate} onChange={(e) => setProjectForm({ ...projectForm, startDate: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="date" value={projectForm.endDate} onChange={(e) => setProjectForm({ ...projectForm, endDate: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <textarea placeholder="Deliverables" value={projectForm.deliverables} onChange={(e) => setProjectForm({ ...projectForm, deliverables: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2" />
                  <textarea placeholder="Google Drive / Document Links (paste folder URL here)" value={projectForm.documentLinks} onChange={(e) => setProjectForm({ ...projectForm, documentLinks: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2" />
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={addProject} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">Save Project</button>
                  <button onClick={() => setShowProjectForm(false)} className="bg-slate-300 text-slate-900 px-4 py-2 rounded-lg hover:bg-slate-400 transition">Cancel</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredProjects.map(project => (
                <div key={project.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-lg text-slate-900">{project.name}</h3>
                      <p className="text-sm text-slate-600">{getClientName(project.client_id)}</p>
                    </div>
                    <button onClick={() => deleteProject(project.id)} className="text-slate-400 hover:text-red-600 transition"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    {project.startDate && <p><span className="font-medium">Start:</span> {project.startDate}</p>}
                    {project.endDate && <p><span className="font-medium">End:</span> {project.endDate}</p>}
                    {project.deliverables && <p><span className="font-medium">Deliverables:</span> {project.deliverables}</p>}
                    {project.documentLinks && (
                      <p>
                        <span className="font-medium">📁 Documents:</span>{' '}
                        <a href={project.documentLinks} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                          View Folder
                        </a>
                      </p>
                    )}
                  </div>
                  <select value={project.status} onChange={(e) => updateProjectStatus(project.id, e.target.value)} className={`px-3 py-1 rounded text-sm font-medium border-0 ${project.status === 'active' ? 'bg-green-100 text-green-800' : project.status === 'completed' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    <option value="active">Active</option>
                    <option value="on-hold">On Hold</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              ))}
            </div>
            {filteredProjects.length === 0 && <p className="text-center text-slate-500 py-12">No projects yet.</p>}
          </div>
        )}

        {/* TASKS TAB */}
        {activeTab === 'tasks' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Tasks</h2>
              <button onClick={() => setShowTaskForm(!showTaskForm)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            </div>

            {showTaskForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Task Title" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="date" value={taskForm.dueDate} onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="text" placeholder="Assign To (name)" value={taskForm.assignee} onChange={(e) => setTaskForm({ ...taskForm, assignee: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <select value={taskForm.clientId} onChange={(e) => setTaskForm({ ...taskForm, clientId: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Link to Client (optional)</option>
                    {clients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                  <select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={addTask} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition">Save Task</button>
                  <button onClick={() => setShowTaskForm(false)} className="bg-slate-300 text-slate-900 px-4 py-2 rounded-lg hover:bg-slate-400 transition">Cancel</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {filteredTasks.map(task => (
                <div key={task.id} className="bg-white rounded-lg shadow p-4 hover:shadow-lg transition border-l-4" style={{borderColor: task.priority === 'high' ? '#ef4444' : task.priority === 'medium' ? '#f59e0b' : '#10b981'}}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={task.status === 'completed'}
                          onChange={(e) => updateTaskStatus(task.id, e.target.checked ? 'completed' : 'pending')}
                          className="w-5 h-5 rounded text-blue-600"
                        />
                        <div className="flex-1">
                          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>{task.title}</p>
                          <div className="flex gap-3 text-sm text-slate-500 mt-1">
                            {task.dueDate && <span>Due: {task.dueDate}</span>}
                            {task.assignee && <span>→ {task.assignee}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => deleteTask(task.id)} className="text-slate-400 hover:text-red-600 transition ml-4"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            {filteredTasks.length === 0 && <p className="text-center text-slate-500 py-12">No tasks yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessCRM;
